/**
 * About Slice
 *
 * Tabbed section: each tab has Nav Item (label), and content: Heading, Copy, Tags, Button.
 */

import React, { useState } from "react";
import { graphql } from "gatsby";
import { motion } from "framer-motion";
import { PrismicRichText } from "@prismicio/react";
import Button from "../../components/Button";
import BackgroundStaticVideo from "../../components/BackgroundStaticVideo";
import * as styles from "./About.module.scss";

export const About = ({ slice }) => {
  const { color_scheme, show_background_static_video, heading } = slice?.primary || {};
  const items = slice?.items || [];
  const [activeIndex, setActiveIndex] = useState(0);

  if (!items.length) return null;

  const navContainerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
        staggerChildren: 0.1,
      },
    },
  };

  const navItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <section
      className={styles.about}
      data-color-scheme={color_scheme || "white"}
      data-background-static-video={show_background_static_video ? "true" : undefined}
      id="about"
    >
      {show_background_static_video && <BackgroundStaticVideo />}
      <div className={`page-width--slim padding-section-vertical ${styles.wrapper}`}>
        {heading?.richText && (
          <motion.div
            className={styles.header}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <PrismicRichText
              field={heading.richText}
              components={{
                heading2: ({ children }) => (
                  <h2 className="h2 mb-08">{children}</h2>
                ),
              }}
            />
          </motion.div>
        )}

        <div className={styles.container}>
          {/* Tab nav */}
          <motion.div
            className={styles.navContainer}
            variants={navContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <nav className={styles.nav} aria-label="About sections">
              <ul className={styles.navList}>
                {items.map((item, index) => (
                  <motion.li
                    key={index}
                    variants={navItemVariants}
                  >
                      <button
                        type="button"
                        className={styles.navButton}
                        aria-selected={activeIndex === index}
                        aria-controls={`about-panel-${index}`}
                        id={`about-tab-${index}`}
                        onClick={() => setActiveIndex(index)}
                      >
                        {item.nav_item || `Tab ${index + 1}`}
                      </button>
                    </motion.li>
                  ))}
                </ul>
            </nav>
          </motion.div>

          {/* Tab panels */}
          <motion.div
            className={styles.panels}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {items.map((item, index) => (
              <div
                key={index}
                id={`about-panel-${index}`}
                role="tabpanel"
                aria-labelledby={`about-tab-${index}`}
                hidden={activeIndex !== index}
                className={styles.panel}
              >
                {item.heading?.richText && (
                  <PrismicRichText
                    field={item.heading.richText}
                    components={{
                      heading2: ({ children }) => (
                        <h2 className={styles.heading}>{children}</h2>
                      ),
                    }}
                  />
                )}
                {item.copy?.richText && (
                  <div className={styles.copy}>
                    <PrismicRichText field={item.copy.richText} />
                  </div>
                )}
                {/* Tags removed - Prismic API doesn't support nested Groups in slice repeat zones */}
                {item.button_text && (
                  <div className={styles.cta}>
                    <Button
                      variant="solid green"
                      href={item.button_link?.url}
                      target={item.button_link?.target}
                    >
                      {item.button_text}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment HomepageDataBodyAbout on PrismicHomepageDataBodyAbout {
    primary {
      heading {
        richText
        text
      }
      color_scheme
      show_background_static_video
    }
    items {
      nav_item
      heading {
        richText
        text
      }
      copy {
        richText
        text
      }
      button_text
      button_link {
        url
        target
        link_type
      }
    }
  }
  fragment AboutPageDataBodyAbout on PrismicAboutPageDataBodyAbout {
    primary {
      heading {
        richText
        text
      }
      color_scheme
      show_background_static_video
    }
    items {
      nav_item
      heading {
        richText
        text
      }
      copy {
        richText
        text
      }
      button_text
      button_link {
        url
        target
        link_type
      }
    }
  }
`;
